// ============================================================
// DEFENCE PROBABILITY ENGINE - Defender statistics calculator.
// Hybrid analytical (dodge/parry) + Monte Carlo (chain/damage/survival).
// Pure TypeScript, zero React dependency.
// ============================================================
import { helplessAgainst } from './helpless';
import { calcDoS } from './dice';
import { exposedDos, onHitExposure, attackGate } from './exposures';
import { applyUnnaturalDos, DEFAULT_UNNATURAL_DOS_MODE } from './unnaturalDos';
import type { UnnaturalDosMode } from './unnaturalDos';
import { calcModifiedSkill, calcJamThreshold, calcTotalHits, calcEffectivePen, hitMode } from './attackCalc';
import { rollBurst } from './attackResolution';
import { afterHitDamage } from './afterHitDamage';
import { getBodyConfig } from './bodyConfig';
import { burstLocations } from './multipleHits';
import { getAvailableDefenceSteps, resolveDefenceStep, totalAvoided } from './defenceChain';
import { accurateBonusDice } from './weaponSpecials';
import { applyDefenderMods } from './defenceCalc';
import { attackSkillMod, buildHistogram, survivalEstimate, withForecastDice } from './probability';
import type { Slot, Target, DefenderCustomMod } from '../types';
import type { DefenceStepResult } from './defenceChain';
import type { HistogramBin } from './probability';
import type { ConditionModContext, ConditionModOptions } from './conditionModifiers';

// ────────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────────

/** Degrees-of-Success distribution for a single reaction. */
export interface ReactionProbability {
  /** P(success) [0,1]. */
  successChance: number;
  /** P(N DoS) - index 0 = P(fail), index 1 = P(0 DoS hit), etc. */
  dosDistribution: number[];
  /** Expected DoS when succeeding. */
  expectedDoS: number;
  /** Effective skill used (after modifiers). */
  effectiveSkill: number;
}

/** Defence chain Monte Carlo results (slide 3). */
export interface DefenceChainDistribution {
  /** P(avoid exactly N hits). Index = N. */
  avoidedDistribution: number[];
  /** Expected number of hits avoided. */
  expectedAvoided: number;
  /** Total incoming hits per simulated attack. */
  incomingHits: number;
}

/** Damage distribution after defence (slide 4). */
export interface DamageAfterDefence {
  /** Histogram bins. */
  bins: HistogramBin[];
  /** Mean total damage after defence. */
  mean: number;
  /** Median total damage. */
  median: number;
  /** 95th percentile. */
  p95: number;
}

/** Defence survival curve (slide 5). */
export interface DefenceSurvivalEstimate {
  /** Survival probability after N attacks. survivalCurve[0] = 1.0. */
  survivalCurve: number[];
  /** Average attacks to kill. */
  meanAttacks: number;
  /** Max attacks simulated. */
  maxAttacks: number;
}

export interface DefenceProbabilityResult {
  dodge: ReactionProbability;
  parry: ReactionProbability;
  /** null when no attacker slot provided. */
  chain: DefenceChainDistribution | null;
  /** null when no attacker slot provided. */
  damage: DamageAfterDefence | null;
  /** null when no attacker slot provided. */
  survival: DefenceSurvivalEstimate | null;
}

export interface DefenceProbabilityInput {
  target: Target;
  /** The attacker's re-roll damage pill - the roll keeps the better of two damage rolls (v8.392.4). */
  rerollDamage?: boolean;
  defenderCustomMods: DefenderCustomMod[];
  slot?: Slot | null;
  fireMode?: string;
  isMelee?: boolean;
  attackMod?: number;
  rangeBand?: string;
  hatred?: boolean;
  damageBonusMelee?: number;
  damageBonusRanged?: number;
  attackerSpecials?: string[];
  /** The attacker's team tag (v8.368.0): a `@hostile` or `@ally` exposure is read strictly, so the forecast must know it. */
  attackerTeamTag?: string | null;
  /** The attacker's side (2026-10-23); absent = the tag. */
  attackerSideTag?: string | null;
  /** The attacker's magnitude when it is a horde (bodyShape `attackingMagnitude`): +1 hit per 10, as the roll. */
  attackerMagnitude?: number;
  /** The attacker's reaction exposures against this defender (v8.368.3), as the freeze would set them. */
  attackerReactionMods?: { dodge: number; parry: number } | null;
  /**
   * The attacker as the roll reads them (2026-10-20): their conditions and the auras they
   * stand under. With the condition rule on, the to-hit sum is the attack forecast's own
   * (`attackSkillMod`: their Stunned, the target's Painted, a Rallying aura), and the auras
   * reach the cannot-miss, degrees, hits and jam reads as they do in the roll.
   */
  attackerConditions?: ConditionModContext;
  /** Unnatural DoS house rule - MUST mirror the live math. Reaction-side ×N flows from Target fields; these are the ATTACK-side inputs. */
  unnaturalDosMode?: UnnaturalDosMode;
  skillCharValue?: number;
  skillUnnaturalMultiplier?: number;
  /** Whether Aim is toggled on the attacker - gates Accurate's +10 and bonus dice (v8.197.14). */
  aimed?: boolean;
  /** Condition-modifier house rule - MUST mirror the live defence chain (reaction gates + skill penalties). */
  applyConditionMods?: boolean;
  /**
   * Which of those modifiers the table has switched off - MUST mirror the
   * live chain too, or the odds quoted before the roll disagree with it.
   */
  condOptions?: ConditionModOptions;
  /** The forecast's own dice, as the attack forecast's (probability.ts withForecastDice). */
  rng?: () => number;
}

// ────────────────────────────────────────────────────────────
// Configuration
// ────────────────────────────────────────────────────────────

const CHAIN_ITERATIONS = 1000;
const DAMAGE_ITERATIONS = 1000;
const DAMAGE_BIN_COUNT = 15;
const SURVIVAL_COMBATS = 500;
const SURVIVAL_MAX_ATTACKS = 30;

// ────────────────────────────────────────────────────────────
// Analytical: Dodge probability (slide 1)
// ────────────────────────────────────────────────────────────

/**
 * Compute exact P(dodge success) and DoS distribution
 * by iterating all 100 d100 outcomes.
 */
export function computeDodgeProbability(
  effectiveDodge: number,
  unnatural?: ReactionUnnaturalInput,
): ReactionProbability {
  return computeReactionProbability(effectiveDodge, unnatural);
}

// ────────────────────────────────────────────────────────────
// Analytical: Parry probability (slide 2)
// ────────────────────────────────────────────────────────────

/**
 * Compute exact P(parry success) and DoS distribution
 * by iterating all 100 d100 outcomes.
 */
export function computeParryProbability(
  effectiveParry: number,
  unnatural?: ReactionUnnaturalInput,
): ReactionProbability {
  return computeReactionProbability(effectiveParry, unnatural);
}

// ────────────────────────────────────────────────────────────
// Monte Carlo: Defence chain (slide 3)
// ────────────────────────────────────────────────────────────

/**
 * Simulate attacks and trace the full defence chain to determine
 * how many hits are avoided. Uses getAvailableDefenceSteps +
 * resolveDefenceStep from defenceChain.ts.
 */
export function computeDefenceChainDistribution(
  input: DefenceProbabilityInput,
  iterations: number = CHAIN_ITERATIONS,
): DefenceChainDistribution | null {
  if (!input.slot || input.slot.isEmpty) return null;
  const slot = input.slot;
  const target = withReactionMods(input);
  const attacks = withForecastDice(input, (seeded) =>
    Array.from({ length: iterations }, () => defendedAttack(slot, target, seeded)),
  );
  const freq = new Map<number, number>(); // avoided count → frequency
  let totalAv = 0;
  let incomingSum = 0;
  let hitAttacks = 0;
  for (const { numHits, avoided } of attacks) {
    if (numHits > 0) {
      hitAttacks++;
      incomingSum += numHits;
      totalAv += avoided;
    }
    freq.set(avoided, (freq.get(avoided) || 0) + 1);
  }
  return {
    avoidedDistribution: distributionOf(freq, iterations),
    expectedAvoided: totalAv / iterations,
    incomingHits: hitAttacks > 0 ? incomingSum / hitAttacks : 0,
  };
}

/** One attack rolled and defended: its hits, and how many the chain avoided (none traced on a miss). */
function defendedAttack(slot: Slot, target: Target, input: DefenceProbabilityInput) {
  const { numHits } = simulateAttack(slot, target, input);
  if (numHits === 0) return { numHits, avoided: 0 };
  const { completedSteps } = traceDefenceChain(defendingTarget(target, input), numHits, input);
  return { numHits, avoided: totalAvoided(completedSteps) };
}

/** Index = avoided count, value = its share of the iterations. */
function distributionOf(freq: Map<number, number>, iterations: number): number[] {
  const maxAv = Math.max(...freq.keys(), 0);
  const avoidedDistribution = new Array(maxAv + 1).fill(0);
  for (const [av, count] of freq) avoidedDistribution[av] = count / iterations;
  return avoidedDistribution;
}

// ────────────────────────────────────────────────────────────
// Monte Carlo: Damage after defence (slide 4)
// ────────────────────────────────────────────────────────────

/**
 * Simulate full attack → defence chain → damage on remaining hits.
 * Returns histogram of total damage dealt after defences.
 */
export function computeDamageAfterDefence(
  input: DefenceProbabilityInput,
  iterations: number = DAMAGE_ITERATIONS,
): DamageAfterDefence | null {
  const { slot } = input;
  if (!slot || slot.isEmpty) return null;

  const samples = withForecastDice(input, (seeded) =>
    Array.from({ length: iterations }, () => simulateOneAttackWithDefence(seeded)),
  ).sort((a, b) => a - b);

  const mean = samples.reduce((s, v) => s + v, 0) / samples.length;
  const median = samples[Math.floor(samples.length / 2)];
  const p95 = samples[Math.floor(samples.length * 0.95)];
  const bins = buildHistogram(samples, DAMAGE_BIN_COUNT);

  return { bins, mean, median, p95 };
}

// ────────────────────────────────────────────────────────────
// Monte Carlo: Defence survival (slide 5)
// ────────────────────────────────────────────────────────────

/**
 * Simulate repeated attacks with defence until the target dies.
 * Returns survival curve.
 */
export function computeDefenceSurvival(
  input: DefenceProbabilityInput,
  combats: number = SURVIVAL_COMBATS,
  maxAttacks: number = SURVIVAL_MAX_ATTACKS,
): DefenceSurvivalEstimate | null {
  const { slot, target } = input;
  if (!slot || slot.isEmpty) return null;
  return withForecastDice(input, (seeded) =>
    survivalEstimate(() => simulateOneAttackWithDefence(seeded), target.wounds.current, combats, maxAttacks),
  );
}

// ────────────────────────────────────────────────────────────
// Orchestrator
// ────────────────────────────────────────────────────────────

/**
 * Compute all defence probability results.
 * Slides 1-2 always present. Slides 3-5 only when slot provided.
 */
export function computeDefenceProbabilities(input: DefenceProbabilityInput): DefenceProbabilityResult {
  const { target, defenderCustomMods } = input;

  const baseDodge = target.dodge?.agility || 0;
  const baseParry = target.parry?.weaponSkill || 0;
  const { effectiveDodge, effectiveParry } = applyDefenderMods(baseDodge, baseParry, defenderCustomMods);

  return {
    dodge: computeDodgeProbability(effectiveDodge),
    parry: computeParryProbability(effectiveParry),
    chain: computeDefenceChainDistribution(input),
    damage: computeDamageAfterDefence(input),
    survival: computeDefenceSurvival(input),
  };
}

// ────────────────────────────────────────────────────────────
// Internal helpers
// ────────────────────────────────────────────────────────────

/** The reaction side of the unnatural DoS house rule, for the analytical odds. */
export interface ReactionUnnaturalInput {
  charValue: number;
  multiplier: number;
  mode: UnnaturalDosMode;
}

/** A successful reaction's DoS on this roll, with the unnatural house rule when given. */
function reactionDos(skill: number, roll: number, unnatural?: ReactionUnnaturalInput): number {
  const dos = calcDoS(skill, roll);
  if (!unnatural) return dos;
  return applyUnnaturalDos(dos, roll, skill, unnatural.charValue, unnatural.multiplier, unnatural.mode).dos;
}

/**
 * Compute reaction probability for a given effective skill.
 * Iterates all 100 d100 outcomes for exact probabilities.
 */
function computeReactionProbability(effectiveSkill: number, unnatural?: ReactionUnnaturalInput): ReactionProbability {
  const clamped = Math.max(0, Math.min(effectiveSkill, 100));
  const dosFreq = new Map<number, number>(); // DoS → frequency
  let successCount = 0;
  let dosSum = 0;
  for (let roll = 1; roll <= clamped && roll <= 100; roll++) {
    successCount++;
    const dos = reactionDos(clamped, roll, unnatural);
    dosFreq.set(dos, (dosFreq.get(dos) || 0) + 1);
    dosSum += dos;
  }
  return {
    successChance: successCount / 100,
    dosDistribution: dosDistributionOf(dosFreq, successCount),
    expectedDoS: successCount > 0 ? dosSum / successCount : 0,
    effectiveSkill: clamped,
  };
}

/** index 0 = P(fail), index 1+ = P(DoS = index - 1). */
function dosDistributionOf(dosFreq: Map<number, number>, successCount: number): number[] {
  const maxDoS = dosFreq.size > 0 ? Math.max(...dosFreq.keys()) : 0;
  const dosDistribution = new Array(maxDoS + 2).fill(0);
  dosDistribution[0] = (100 - successCount) / 100; // P(fail)
  for (const [dos, count] of dosFreq) dosDistribution[dos + 1] = count / 100;
  return dosDistribution;
}

/** The attacker's reaction exposures ride the target here as they ride the frozen one (v8.368.3). */
function withReactionMods(input: DefenceProbabilityInput): Target {
  const { target, attackerReactionMods } = input;
  return attackerReactionMods ? { ...target, attackerReactionMods } : target;
}

/** The attack side of the input, absent fields at their defaults. */
function attackOptions(input: DefenceProbabilityInput) {
  return {
    fireMode: input.fireMode || 'single',
    isMelee: input.isMelee ?? false,
    attackMod: input.attackMod ?? 0,
    rangeBand: input.rangeBand || 'normal',
    hatred: input.hatred ?? false,
  };
}

/** The attacker as the roll sees them, defaulting to just their team when the caller knows no more. */
function attackerOf(input: DefenceProbabilityInput): ConditionModContext {
  return (
    input.attackerConditions ?? {
      conditions: [],
      teamTag: input.attackerTeamTag ?? null,
      sideTag: input.attackerSideTag ?? null,
    }
  );
}

/** The attack's to-hit sum, with the condition rule's terms when the table applies them - the attack forecast's own. */
function forecastAttackMod(target: Target, input: DefenceProbabilityInput): number {
  const { attackMod, isMelee, rangeBand } = attackOptions(input);
  return attackSkillMod({
    attackMod,
    attackerConditions: attackerOf(input),
    applyConditionMods: input.applyConditionMods,
    condOptions: input.condOptions,
    target,
    isMelee,
    rangeBand,
  });
}

/** Read with the attacker's team (v8.368.0) and the auras they stand under (2026-10-20), as the roll reads them. */
function attackerReader(input: DefenceProbabilityInput, isMelee: boolean) {
  return {
    teamTag: input.attackerTeamTag ?? null,
    sideTag: input.attackerSideTag ?? null,
    isMelee,
    auras: input.attackerConditions?.auras,
  };
}

/**
 * Roll one attack to hit and count its hits (before defence); 0 on a miss
 * or a jam. The d100 is drawn even for an auto-hit, whose DoS comes from
 * it - unlike the attack forecast's synthetic 0.
 */
function simulateAttack(slot: Slot, target: Target, input: DefenceProbabilityInput): { numHits: number; dos: number } {
  const { fireMode, isMelee, rangeBand, hatred } = attackOptions(input);
  const aimed = input.aimed ?? false;
  const modifiedSkill = calcModifiedSkill(
    slot,
    forecastAttackMod(target, input),
    target.size,
    rangeBand,
    isMelee,
    hatred,
    false,
    false,
    false,
    aimed,
  );
  const reader = attackerReader(input, isMelee);
  const targetOnHit = onHitExposure(target, reader);
  const autoHit = attackGate(target, reader).autoHit;
  const jamThreshold = calcJamThreshold(slot.specialRules || [], isMelee, false, { jamFloor: targetOnHit.jam });
  const roll = Math.floor((input.rng ?? Math.random)() * 100) + 1;
  // A failed roll against a helpless target still lands (engine/helpless); a jam never does.
  const helpless = helplessAgainst(target, isMelee, input.condOptions);
  if (!autoHit && (roll >= jamThreshold || (roll > modifiedSkill && !helpless))) return { numHits: 0, dos: 0 };
  const dos = exposedDos(attackRollDos(modifiedSkill, roll, input), target, reader);
  const { effectiveMode, rof } = hitMode(fireMode, slot, isMelee, false);
  const { hits } = calcTotalHits(dos, effectiveMode, rof, slot.specialRules || [], rangeBand, {
    isMelee,
    isHorde: target.horde?.isHorde || false,
    shift: targetOnHit.hits,
    attackerMagnitude: input.attackerMagnitude ?? 0,
  });
  return { numHits: hits, dos };
}

/** The attack's DoS with the unnatural house rule - the target's `dos` exposure (v8.350.0) comes after. */
function attackRollDos(modifiedSkill: number, roll: number, input: DefenceProbabilityInput): number {
  const charValue = input.skillCharValue ?? 0;
  const multiplier = input.skillUnnaturalMultiplier ?? 1;
  const mode = input.unnaturalDosMode ?? DEFAULT_UNNATURAL_DOS_MODE;
  return applyUnnaturalDos(calcDoS(modifiedSkill, roll), roll, modifiedSkill, charValue, multiplier, mode).dos;
}

/** The target with its custom defender modifiers folded into the skills the chain rolls. */
function defendingTarget(target: Target, input: DefenceProbabilityInput): Target {
  const baseDodge = target.dodge?.agility || 0;
  const baseParry = target.parry?.weaponSkill || 0;
  const { effectiveDodge, effectiveParry } = applyDefenderMods(baseDodge, baseParry, input.defenderCustomMods);
  return {
    ...target,
    dodge: { ...target.dodge, agility: effectiveDodge },
    parry: { ...target.parry, weaponSkill: effectiveParry },
  };
}

/**
 * Trace the defence chain against `numHits`: the AI always picks the first
 * available step (best strategy) until none is left or every hit is avoided.
 */
function traceDefenceChain(modTarget: Target, numHits: number, input: DefenceProbabilityInput) {
  const isMelee = input.isMelee ?? false;
  const applyConditionMods = input.applyConditionMods ?? false;
  const counter = modTarget.counterAttackAvailable ?? false;
  const mode = input.unnaturalDosMode ?? DEFAULT_UNNATURAL_DOS_MODE;
  const completedSteps: DefenceStepResult[] = [];
  let remaining = numHits;
  for (;;) {
    const available = getAvailableDefenceSteps(
      modTarget,
      isMelee,
      completedSteps,
      remaining,
      input.attackerSpecials,
      applyConditionMods,
      input.condOptions,
    );
    if (available.length === 0) break;
    const result = resolveDefenceStep(available[0], remaining, counter, mode);
    completedSteps.push(result);
    remaining -= result.avoided;
    if (remaining <= 0) break;
  }
  return { completedSteps, remaining };
}

/**
 * Simulate a full attack with defence chain, returning total damage dealt.
 */
function simulateOneAttackWithDefence(input: DefenceProbabilityInput): number {
  const { slot } = input;
  if (!slot || slot.isEmpty) return 0;
  const target = withReactionMods(input);
  const { numHits, dos } = simulateAttack(slot, target, input);
  if (numHits === 0) return 0;
  const { remaining } = traceDefenceChain(defendingTarget(target, input), numHits, input);
  if (remaining <= 0) return 0;
  return damageOfHits(slot, target, input, dos, remaining);
}

/**
 * Damage for the hits the chain let through - the first at a drawn location, the rest by the Multiple Hits table
 * (a called location is not modelled here: the defence forecast has no aim input, as before),
 * through the live burst loop - wounds run down and an overloaded field
 * stays down exactly as they do at the table.
 */
function damageOfHits(slot: Slot, target: Target, input: DefenceProbabilityInput, dos: number, hits: number): number {
  const bodyConfig = getBodyConfig(target.bodyType || 'humanoid');
  const { hits: results } = rollBurst({
    shared: sharedHitParams(slot, target, input, dos),
    target,
    numHits: hits,
    wounds: target.wounds.current,
    // The first hit drawn, the rest by the Multiple Hits table, as the roll does for an un-aimed shot.
    locationAt: burstLocations(bodyConfig),
  });
  const landed = results.reduce((total, r) => total + r.effectiveDamage, 0);
  // What the roll adds after the hits - Toxic, Corrosive (v8.392.5).
  return landed + afterHitDamage(target, results, dos, slot);
}

/** What every surviving hit passes to calcSingleHitDamage. */
function sharedHitParams(slot: Slot, target: Target, input: DefenceProbabilityInput, dos: number) {
  const { fireMode, isMelee, rangeBand, hatred } = attackOptions(input);
  return {
    slot,
    target,
    pen: calcEffectivePen(slot.penetration, slot.specialRules || [], dos, rangeBand).pen,
    dos,
    accurateBonus: accurateBonusDice(slot.specialRules || [], fireMode, dos, input.aimed ?? false),
    rangeBand,
    isMelee,
    isShortOrCloser: rangeBand === 'pointBlank' || rangeBand === 'short',
    hatred,
    damageBonusMelee: input.damageBonusMelee ?? 0,
    damageBonusRanged: input.damageBonusRanged ?? 0,
    rerollDamage: input.rerollDamage ?? false,
    helpless: helplessAgainst(target, isMelee, input.condOptions),
    bodyType: target.bodyType,
  };
}
